/**
 * Common screen shell: page background, Telegram chrome sync, the native BackButton on
 * sub-screens, the offline banner, and bottom padding that clears whichever bar is showing.
 */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackButton, useChrome, useDisableVerticalSwipes } from '../hooks/useTg';
import { useOnline } from '../providers/AppProviders';
import { OfflineBanner } from './feedback';
import { TabbarSpacer } from './Tabbar';

export function Screen({
  children,
  /** Root tabs keep the tab bar; sub-screens get Telegram's back arrow instead. */
  root = false,
  /** Extra space for a screen that also renders Telegram's BottomButton. */
  hasBottomButton = false,
  chrome = 'surface',
  className = '',
  contentClassName = '',
  onBack,
}: {
  children: ReactNode;
  root?: boolean;
  hasBottomButton?: boolean;
  chrome?: 'surface' | 'page';
  className?: string;
  /** Extra classes on the growing content area — e.g. the chat screen anchors to the bottom. */
  contentClassName?: string;
  onBack?: () => void;
}) {
  const navigate = useNavigate();
  const online = useOnline();

  useChrome({ header: chrome, bottomBar: root ? 'surface' : 'page' });
  useDisableVerticalSwipes(true);
  useBackButton(root ? null : (onBack ?? (() => navigate(-1))));

  return (
    <div className={`flex min-h-full flex-col bg-page ${className}`}>
      {!online && <OfflineBanner />}
      <div className={`flex-1 ${contentClassName}`}>{children}</div>
      {root ? (
        <TabbarSpacer />
      ) : hasBottomButton ? (
        <div aria-hidden="true" className="h-6 flex-none" />
      ) : (
        <div aria-hidden="true" className="flex-none" style={{ height: 'var(--pv-safe-bottom)' }} />
      )}
    </div>
  );
}

/** Standard gutter wrapper. */
export function Padded({
  children,
  className = '',
  testId,
}: {
  children: ReactNode;
  className?: string;
  /** A stable hook for the e2e harnesses — class names change with every restyle. */
  testId?: string;
}) {
  return (
    <div className={`px-gutter ${className}`} {...(testId ? { 'data-testid': testId } : {})}>
      {children}
    </div>
  );
}
